import { useTranslation } from 'react-i18next';
import { Receipt, FileText } from 'lucide-react';

import { formatDateByDayjs } from '@/utils';
import { useFetchTransactionByOrderID } from '@/hooksApi';
import { OrderTransaction, TransactionStatusEnum } from '@/types';
import useLanguageStore from '@/stores/useLanguageStore';
import { SectionHeader } from './components/SectionHeader';

const getStatusBadgeClass = (slug?: TransactionStatusEnum | string) => {
  switch (slug) {
    case TransactionStatusEnum.PAID:
    case TransactionStatusEnum.ENABLED:
      return 'bg-emerald-100 text-emerald-700';
    case TransactionStatusEnum.PENDING:
      return 'bg-amber-100 text-amber-700';
    case TransactionStatusEnum.CANCEL:
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-sd-muted text-sd-muted-foreground';
  }
};

const TransactionItem = ({ tx, t }: { tx: OrderTransaction; t: (key: string) => unknown }) => (
  <div className='flex items-center gap-3 py-2.5'>
    <p className='text-[11px] text-sd-muted-foreground whitespace-nowrap shrink-0 w-[110px]'>
      {formatDateByDayjs(tx.created_at, 'D MMM YYYY, HH:mm')}
    </p>
    <p className='text-[11px] font-mono text-sd-muted-foreground truncate shrink-0 w-[120px]'>
      {tx.platform_code || tx.uid}
    </p>
    <p className='text-[12px] font-medium text-sd-foreground truncate flex-1 min-w-0'>
      {String(t(`transaction_title.${tx.subject}`))}
    </p>
    <p
      className={`text-[12px] font-semibold whitespace-nowrap shrink-0 text-end w-[100px] ${
        Number(tx.amount) < 0
          ? 'text-red-600'
          : Number(tx.amount) > 0
            ? 'text-emerald-600'
            : 'text-sd-foreground'
      }`}
    >
      {Number(tx.amount) > 0 ? '+' : ''}
      {Number(tx.amount).toLocaleString()} {String(t('iqd'))}
    </p>
    <div className='flex items-center justify-end gap-1.5 shrink-0 w-[80px]'>
      {tx.is_correction === 1 && (
        <span className='text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700'>
          {String(t('correction'))}
        </span>
      )}
      <span
        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getStatusBadgeClass(tx.status?.slug)}`}
      >
        {String(t(`transaction_status.${tx.status?.title}`))}
      </span>
    </div>
  </div>
);

const TransactionListSection = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();

  const { data: transactionList, isFetching } = useFetchTransactionByOrderID(orderId, language);

  const { correctionTransactions, regularTransactions } = (
    transactionList?.object.items || []
  ).reduce(
    (acc, transaction) => {
      if (transaction.is_correction === 1) {
        acc.correctionTransactions.push(transaction);
      } else {
        acc.regularTransactions.push(transaction);
      }
      return acc;
    },
    {
      correctionTransactions: [] as OrderTransaction[],
      regularTransactions: [] as OrderTransaction[],
    },
  );

  const renderTransactionList = (transactions: OrderTransaction[]) =>
    isFetching ? (
      <div className='flex items-center justify-center py-8'>
        <div className='h-5 w-5 rounded-full border-2 border-sd-primary border-t-transparent animate-spin' />
      </div>
    ) : transactions.length === 0 ? (
      <p className='text-[12px] text-sd-muted-foreground text-center py-4'>
        {String(t('no_data'))}
      </p>
    ) : (
      <div>
        <div className='flex items-center gap-3 py-2 border-0 border-b border-solid border-sd-border'>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground shrink-0 w-[110px]'>{String(t('date'))}</p>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground shrink-0 w-[120px]'>{String(t('transaction_id'))}</p>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground flex-1 min-w-0'>{String(t('type'))}</p>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground shrink-0 text-end w-[100px]'>{String(t('amount'))}</p>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground shrink-0 text-end w-[80px]'>{String(t('status'))}</p>
        </div>
        <div className='divide-y divide-sd-border'>
          {transactions.map((tx, i) => (
            <TransactionItem key={tx.uid || i} tx={tx} t={t} />
          ))}
        </div>
      </div>
    );

  // O-19: Hide transaction/correction cards entirely if empty
  const hasRegular = isFetching || regularTransactions.length > 0;
  const hasCorrection = isFetching || correctionTransactions.length > 0;

  if (!hasRegular && !hasCorrection) return null;

  return (
    <>
      {/* Regular Transactions — hidden when empty */}
      {hasRegular && (
        <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
          <SectionHeader icon={Receipt} title={String(t('transactions'))} />
          {renderTransactionList(regularTransactions)}
        </section>
      )}

      {/* Correction Transactions — hidden when empty */}
      {hasCorrection && (
        <section className='rounded-2xl border border-sd-border bg-sd-card p-5'>
          <SectionHeader icon={Receipt} title={String(t('correction_section_title'))} />
          {renderTransactionList(correctionTransactions)}
        </section>
      )}
    </>
  );
};

export default TransactionListSection;
